import { createElement, forwardRef } from "react";
import { Link as RouterLink } from "@remix-run/react";

import { useLabels } from "~/hooks/useLabels";
import { isMobileNative } from "~/utils/helpers";
import { sendMessageToHost } from "~/utils/events";
import { getFullUrl } from "~/utils/url";

import type { ReactNode, MouseEvent as ReactMouseEvent } from "react";
import type { RemixLinkProps } from "@remix-run/react/dist/components";
import type { NavigationActionType, PageStencil } from "~/utils/events";

export type CustomNavigationActionType = "replacePresentFullscreen";
export type NativeBehavior = NavigationActionType | CustomNavigationActionType;

export interface LinkProps extends RemixLinkProps {
  children: ReactNode;
  className?: string;
  nativeBehavior?: NativeBehavior;
  nativeStencil?: PageStencil;
}

const DEFAULT_LABELS = {
  "prompt.login.title": "Unlock the whole class experience...",
  "prompt.login.description":
    "Save your progress and get access to more features!",
};

/** Performs some clean-up before the view is unloaded */
function cleanUp() {
  // Ensure the screen sleep timer is re-enabled
  sendMessageToHost({
    ui: {
      setScreenTimerDisabled: {
        disabled: false,
      },
    },
  });

  // Ensure the tab bar is reset
  sendMessageToHost({
    ui: {
      setTabBarVisible: {
        animated: true,
        visible: true,
      },
    },
  });
}

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function LinkImpl(
  {
    children,
    className,
    nativeBehavior,
    nativeStencil,
    reloadDocument,
    style,
    target,
    to,
    onClick,
    ...rest
  },
  ref,
) {
  const { t } = useLabels({ fallback: DEFAULT_LABELS });

  function handleClick(event: ReactMouseEvent<HTMLAnchorElement, MouseEvent>) {
    onClick?.(event);

    // Send a message to the host app
    if (nativeBehavior && isMobileNative()) {
      event.preventDefault();

      const url = getFullUrl(to.toString());

      switch (nativeBehavior) {
        case "dismiss":
          cleanUp();
          sendMessageToHost({
            navigation: {
              dismiss: { all: false },
            },
          });
          break;
        case "pop":
          cleanUp();
          sendMessageToHost({
            navigation: {
              pop: {},
            },
          });
          break;
        case "present":
          sendMessageToHost({
            navigation: {
              present: {
                stencil: nativeStencil,
                url,
              },
            },
          });
          break;
        case "presentFullScreen":
          sendMessageToHost({
            navigation: {
              presentFullScreen: {
                stencil: nativeStencil,
                url,
              },
            },
          });
          break;
        case "replacePresentFullscreen":
          sendMessageToHost(
            {
              navigation: {
                dismiss: { all: true },
              },
            },
            /* options */ {
              completionMessage: {
                navigation: {
                  presentFullScreen: {
                    stencil: nativeStencil,
                    url,
                  },
                },
              },
            },
          );
          break;
        case "presentLink":
          sendMessageToHost({
            navigation: {
              presentLink: {
                url: to as string,
              },
            },
          });
          break;
        case "push":
          sendMessageToHost({
            navigation: {
              push: {
                stencil: nativeStencil,
                url,
              },
            },
          });
          break;
        case "presentLogin":
          sendMessageToHost({
            navigation: {
              presentLogin: {
                title: t("prompt.login.title"),
                description: t("prompt.login.description"),
              },
            },
          });
          break;
        default:
          return;
      }
    }
  }

  const isExternal = to.toString().startsWith("http") && target !== "_self";

  const baseProps = {
    ...rest,
    ref,
    className,
    children,
    style,
    target,
    onClick: handleClick,
  };

  return isExternal
    ? createElement("a", {
        ...baseProps,
        href: to.toString(),
        target: "_blank",
      })
    : createElement(RouterLink, {
        ...baseProps,
        reloadDocument,
        to,
      });
});
